import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Facebook, Instagram } from "lucide-react";
import { api } from "../lib/api";

export const Footer = () => {
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: api.getCategories });
  return (
    <footer className="border-t border-navy-700 bg-navy-900 text-slate-300" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-10 md:grid-cols-4">
          <div>
            <img src="/logo-wide.png" alt="ShoppinMagic" className="h-10 w-auto" />
            <p className="mt-4 text-sm leading-relaxed text-slate-400">
              A curated affiliate marketplace. We find trending products from trusted merchants and make great deals
              appear like magic — we never sell, stock, or ship anything ourselves.
            </p>
            <div className="mt-4 flex gap-3">
              {[
                { icon: Facebook, label: "Facebook", id: "social-facebook", href: "https://www.facebook.com/profile.php?id=61594522890303" },
                { icon: Instagram, label: "Instagram", id: "social-instagram", href: "https://www.instagram.com/shoppinmagicofficial" },
              ].map(({ icon: Icon, label, id, href }) => (
                <a
                  key={id}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={id}
                  className="rounded-full bg-navy-800 p-2 text-gold-400 transition-colors hover:bg-gold-600 hover:text-white"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-white">Shop</h3>
            <ul className="mt-4 space-y-2 text-sm">
              {categories.map((c) => (
                <li key={c.slug}>
                  <Link to={`/category/${c.slug}`} data-testid={`footer-category-${c.slug}`} className="hover:text-gold-400">
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-white">Company</h3>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link to="/about" data-testid="footer-about" className="hover:text-gold-400">About Us</Link></li>
              <li><Link to="/blog" data-testid="footer-blog" className="hover:text-gold-400">Blog</Link></li>
              <li><Link to="/deals" data-testid="footer-deals" className="hover:text-gold-400">Daily Deals</Link></li>
              <li><Link to="/sell" data-testid="footer-sell" className="hover:text-gold-400">Sell on ShoppinMagic</Link></li>
              <li><Link to="/contact" data-testid="footer-contact" className="hover:text-gold-400">Contact</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-white">Legal</h3>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link to="/legal/affiliate-disclosure" data-testid="footer-affiliate-disclosure" className="hover:text-gold-400">Affiliate Disclosure</Link></li>
              <li><Link to="/legal/privacy-policy" data-testid="footer-privacy-policy" className="hover:text-gold-400">Privacy Policy</Link></li>
              <li><Link to="/legal/terms-of-service" data-testid="footer-terms" className="hover:text-gold-400">Terms of Service</Link></li>
              <li><Link to="/legal/refund-policy" data-testid="footer-refund-policy" className="hover:text-gold-400">Refund Policy</Link></li>
              <li><Link to="/legal/cookie-policy" data-testid="footer-cookie-policy" className="hover:text-gold-400">Cookie Policy</Link></li>
            </ul>
          </div>
        </div>
        <div className="mt-10 rounded-xl border border-navy-700 bg-navy-800/60 p-4 text-xs leading-relaxed text-slate-400" data-testid="footer-disclosure">
          <strong className="text-slate-200">Affiliate Disclosure:</strong> ShoppinMagic is a participant in the Amazon
          Services LLC Associates Program and other affiliate programs (Temu, AliExpress, CJ, Impact, ShareASale).
          As an Amazon Associate, we earn from qualifying purchases. When you buy through links on this site, we may
          earn a commission at no additional cost to you. We do not sell, stock, or ship any products — all orders,
          pricing, shipping, and returns are handled by the external merchants.{" "}
          <Link to="/legal/affiliate-disclosure" className="underline hover:text-gold-400">Learn more</Link>
        </div>
        <p className="mt-6 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} ShoppinMagic. All rights reserved.
        </p>
      </div>
    </footer>
  );
};
